// src/chatGPT/utils/markdownParser.js
import { remark } from 'remark';
import remarkGfm from 'remark-gfm';

/**
 * Extracts visualization blocks from markdown text
 * @param {string} markdown - The markdown text
 * @returns {Object} - Object with parsed markdown and extracted viz blocks
 */
export function parseMarkdownWithViz(markdown) {
  const vizPattern = /\\\\\\viz\\\\\\([\s\S]*?)\\\\\\viz\\\\\\/g;
  const vizBlocks = [];
  let index = 0;
  
  // Replace viz blocks with placeholders and store the JSON
  const processedMarkdown = markdown.replace(vizPattern, (match, vizContent) => {
    try {
      const vizData = JSON.parse(vizContent.trim());
      const placeholder = `{{VIZ_PLACEHOLDER_${index}}}`;
      vizBlocks.push({
        id: index,
        placeholder,
        data: vizData
      });
      index++;
      return placeholder;
    } catch (e) {
      console.error('Error parsing viz block:', e);
      return match; // Keep the original if parsing fails
    }
  });
  
  return {
    markdown: processedMarkdown,
    vizBlocks
  };
}

/**
 * Process markdown text with standard plugins
 * @param {string} markdown - The markdown text
 * @returns {string} - Processed markdown
 */
export function processMarkdown(markdown) {
  // Use remark to process the markdown
  const processor = remark().use(remarkGfm);
  const processed = processor.processSync(markdown);
  return processed.toString();
}

/**
 * Formats markdown for proper rendering
 * This can be used to clean up or standardize markdown before rendering
 * @param {string} markdown - Raw markdown text
 * @returns {string} - Cleaned markdown
 */
export function cleanMarkdown(markdown) {
  // Handle common formatting issues
  let cleaned = markdown;
  
  // Fix table alignment issues by ensuring consistent column widths
  cleaned = cleaned.replace(
    /\|([^|\n]*)\|([^|\n]*)\|([^|\n]*)\|/g,
    (match, col1, col2, col3) => {
      return `| ${col1.trim()} | ${col2.trim()} | ${col3.trim()} |`;
    }
  );
  
  // Ensure proper line breaks before and after lists
  cleaned = cleaned.replace(/([^\n])\n[*-]/g, '$1\n\n*');
  
  // Ensure proper line breaks before and after headings
  cleaned = cleaned.replace(/([^\n])\n(#{1,6} )/g, '$1\n\n$2');
  
  // Handle TL;DR section consistently
  cleaned = cleaned.replace(/## TL;DR\n/g, '## TL;DR\n\n');
  
  return cleaned;
}